import React from 'react'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { NFTGallery } from '@/components/NFTGallery'

export const LandingPage: React.FC = () => {
  const previewContainerRef = React.useRef<HTMLDivElement | null>(null)
  const [isPreviewHovered, setIsPreviewHovered] = React.useState(false)

  // Auto-scroll previews horizontally; pause when user hovers
  React.useEffect(() => {
    if (!previewContainerRef.current) return

    const container = previewContainerRef.current
    const scrollStep = 1.5 // pixels per tick
    const intervalMs = 16 // ~60fps

    const id = window.setInterval(() => {
      if (isPreviewHovered) return
      if (!container) return

      // When we reach the end, jump back to start for an infinite loop
      if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 1) {
        container.scrollLeft = 0
      } else {
        container.scrollLeft += scrollStep
      }
    }, intervalMs)

    return () => window.clearInterval(id)
  }, [isPreviewHovered])

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-black/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#d4a853] rounded-lg flex items-center justify-center">
              <span className="text-black font-bold text-sm">N</span>
            </div>
            <span className="font-semibold hidden sm:block">
              NFT<span className="text-[#d4a853]">WebToken</span>
            </span>
          </a>
          <nav className="flex items-center gap-4 text-sm">
            <a href="#how-it-works" className="text-white/70 hover:text-white transition-colors">
              How it works
            </a>
            <a href="#faq" className="text-white/70 hover:text-white transition-colors">
              FAQ
            </a>
            <Button
              size="sm"
              className="hidden sm:inline-flex"
              onClick={() => {
                window.location.href = '/dapp'
              }}
            >
              <span className="flex items-center gap-1">
                Open dApp
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12 space-y-16">
        {/* Hero */}
        <section className="grid lg:grid-cols-[1.3fr,1fr] gap-10 items-center pt-6">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#d4a853] mb-3">
              Beginner Friendly NFT Experience
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              Own a Cosmic NFT  Without Being a Crypto Expert
            </h1>
            <p className="text-white/70 text-base sm:text-lg mb-6 max-w-xl">
              Cosmic Alchemists is a premium NFT collection with a 7day, 90% moneyback guarantee.
              Designed for people who are new to Web3  no complicated jargon, clear steps, and full transparency.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Button
                size="lg"
                className="px-8"
                onClick={() => {
                  window.location.href = '/dapp'
                }}
              >
                <span className="flex items-center gap-2">
                  Mint Your First NFT
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-white/20 text-white/80 hover:text-white"
                onClick={() => {
                  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                How It Works
              </Button>
            </div>
            <p className="text-xs text-white/40">
              ✅ 7Day Refund  b7 ✅ Transparent Pricing  b7 ✅ Secure on Ethereum Sepolia Testnet
            </p>
          </div>

          <Card className="bg-gradient-to-br from-purple-900/60 via-black to-slate-900 border-purple-500/40">
            <div className="p-6 space-y-4">
              <p className="text-sm font-semibold text-purple-200 mb-2">
                Quick Overview
              </p>
              <div className="space-y-3 text-sm text-white/80">
                <p>
                  <span className="text-white">10,000</span> unique NFTs in one collection.
                </p>
                <p>
                  <span className="text-white">7Day refund</span> window after mint: get back 90% of your mint price if you change your mind.
                </p>
                <p>
                  Runs on <span className="text-white">Ethereum Sepolia Testnet</span> so you can learn without risking real funds.
                </p>
                <p>
                  Think of it as a safe playground to understand how wallets, smart contracts and NFTs work endtoend.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* Preview carousel */}
        <section className="space-y-4">
          <h2 className="text-2xl sm:text-3xl font-bold">A Glimpse Into the Collection</h2>
          <p className="text-white/70 max-w-3xl text-sm sm:text-base">
            Here are a few example artworks from the Cosmic Alchemists universe. Minted NFTs are generated and stored
            on-chain, but this gives you a taste of the visual style.
          </p>
          <div className="mt-3 -mx-4 sm:mx-0 overflow-x-auto pb-4" ref={previewContainerRef}>
            <div className="flex gap-4 px-4 sm:px-0 min-w-max">
              {[
                '/preview/Midjourney1.png',
                '/preview/Midjourney2.png',
                '/preview/Midjourney6.png',
                '/preview/Midjourney12.png',
                '/preview/Midjourney29.png',
                '/preview/Midjourney31.png',
                '/preview/Midjourney32.png',
                '/preview/Midjourney33.png',
                '/preview/Midjourney41.png',
                '/preview/Midjourneyas.png',
                '/preview/Midjourneyt.png',
              ].map((src, idx) => (
                <div
                  key={src}
                  className="relative w-60 h-60 flex-shrink-0 rounded-2xl overflow-hidden border border-white/10 bg-slate-900/60 transform transition-transform duration-300 hover:scale-105 cursor-pointer"
                  onMouseEnter={() => setIsPreviewHovered(true)}
                  onMouseLeave={() => setIsPreviewHovered(false)}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={src}
                    alt={`Cosmic Alchemists preview #${idx + 1}`}
                    className="w-full h-full object-cover select-none"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-xs text-white/80">
                    <span className="font-semibold">Preview #{idx + 1}</span>
                    <span className="text-white/60">Concept art</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* NFT Gallery */}
        <NFTGallery />

        {/* What is this */}
        <section id="what-is" className="space-y-4">
          <h2 className="text-2xl sm:text-3xl font-bold">What Is the Cosmic Alchemists NFT Collection?</h2>
          <p className="text-white/70 max-w-3xl text-base sm:text-lg">
            Cosmic Alchemists is a limited collection of 10,000 digital artworks stored on the Ethereum blockchain.
            Each NFT is a unique piece of digital art you can own, stored in your crypto wallet instead of a normal
            website account, and tradeable on NFT marketplaces.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <Card className="bg-white/5 border-white/10">
              <div className="p-4 space-y-2">
                <h3 className="font-semibold">True Digital Ownership</h3>
                <p className="text-sm text-white/70">
                  Your NFT lives in your wallet, not in a centralized database. Only you can move or sell it.
                </p>
              </div>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <div className="p-4 space-y-2">
                <h3 className="font-semibold">Transparent Rules</h3>
                <p className="text-sm text-white/70">
                  The smart contract that powers minting and refunds is public. Anyone can inspect the rules.
                </p>
              </div>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <div className="p-4 space-y-2">
                <h3 className="font-semibold">Beginner Friendly</h3>
                <p className="text-sm text-white/70">
                  Clear, stepbystep flow that shows you exactly what to do, even if this is your first NFT.
                </p>
              </div>
            </Card>
          </div>
        </section>

        {/* Why safe */}
        <section id="how-it-works" className="space-y-6">
          <h2 className="text-2xl sm:text-3xl font-bold">Why It bcs Safe for Beginners</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-green-500/5 border-green-500/30">
              <div className="p-5 space-y-3">
                <h3 className="text-lg font-semibold text-green-300">🛡 7Day Refund Guarantee</h3>
                <p className="text-sm text-white/80">
                  After you mint a Cosmic Alchemists NFT, you have 7 days to change your mind.
                </p>
                <ul className="text-sm text-white/70 list-disc list-inside space-y-1">
                  <li>If you request a refund within 7 days, the contract sends you back 90% of your mint price.</li>
                  <li>The NFT is burned (destroyed) so it no longer exists.</li>
                  <li>The refund logic lives in the smart contract, not in a customer support inbox.</li>
                </ul>
              </div>
            </Card>
            <Card className="bg-purple-500/5 border-purple-500/30">
              <div className="p-5 space-y-3">
                <h3 className="text-lg font-semibold text-purple-200">📉 Fair Pricing with a Dutch Auction</h3>
                <p className="text-sm text-white/80">
                  Instead of one fixed price, the mint price slowly decreases over time.
                </p>
                <ul className="text-sm text-white/70 list-disc list-inside space-y-1">
                  <li>The price starts higher and drops step by step.</li>
                  <li>Everyone sees the same current price in real time.</li>
                  <li>You decide when you feel comfortable to mint.</li>
                </ul>
              </div>
            </Card>
          </div>
        </section>

        {/* Steps */}
        <section id="steps" className="space-y-6">
          <h2 className="text-2xl sm:text-3xl font-bold">How to Get Started in 3 Simple Steps</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="bg-white/5 border-white/10">
              <div className="p-5 space-y-2">
                <p className="text-xs font-semibold text-white/50">Step 1</p>
                <h3 className="font-semibold">Install a Wallet</h3>
                <p className="text-sm text-white/70">
                  Download MetaMask or another trusted wallet app. It works like a login plus a digital safe in one
                  place.
                </p>
              </div>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <div className="p-5 space-y-2">
                <p className="text-xs font-semibold text-white/50">Step 2</p>
                <h3 className="font-semibold">Switch to Sepolia Test Network</h3>
                <p className="text-sm text-white/70">
                  In your wallet, choose the Sepolia test network. You can request free test ETH from a faucet to try
                  the full flow without real money.
                </p>
              </div>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <div className="p-5 space-y-2">
                <p className="text-xs font-semibold text-white/50">Step 3</p>
                <h3 className="font-semibold">Mint Your NFT</h3>
                <p className="text-sm text-white/70">
                  Visit the dApp, connect your wallet, choose a quantity and confirm the transaction. Your NFT appears
                  in the "My NFTs" section.
                </p>
              </div>
            </Card>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="space-y-6">
          <h2 className="text-2xl sm:text-3xl font-bold">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <Card className="bg-white/5 border-white/10">
              <div className="p-4 space-y-2">
                <h3 className="font-semibold text-sm">Do I need real money to try this?</h3>
                <p className="text-sm text-white/70">
                  No. The collection currently runs on the Sepolia test network, which uses test ETH with no real
                  monetary value. It bcs a safe way to learn how NFTs work before you ever touch the Ethereum mainnet.
                </p>
              </div>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <div className="p-4 space-y-2">
                <h3 className="font-semibold text-sm">What happens if I lose my wallet?</h3>
                <p className="text-sm text-white/70">
                  If you lose access to your wallet or recovery phrase, nobody can restore it  not even us. Treat it
                  like a bank account: write down your seed phrase on paper and never share it with anyone.
                </p>
              </div>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <div className="p-4 space-y-2">
                <h3 className="font-semibold text-sm">Can you take my NFT away?</h3>
                <p className="text-sm text-white/70">
                  No. Your NFT lives in your wallet on the blockchain. The only way it can move is if you approve a
                  transaction from your wallet (for example to transfer, sell or refund it).
                </p>
              </div>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <div className="p-4 space-y-2">
                <h3 className="font-semibold text-sm">Is this financial advice?</h3>
                <p className="text-sm text-white/70">
                  No. This project is for educational and entertainment purposes only. NFT prices can go up or down,
                  and there is no guarantee of future value. Never spend more than you bcre prepared to lose.
                </p>
              </div>
            </Card>
          </div>
        </section>

        {/* Final CTA */}
        <section className="border border-white/10 rounded-2xl p-6 sm:p-8 bg-gradient-to-r from-[#d4a853]/10 via-transparent to-purple-500/10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold mb-1">Ready to Try Your First NFT Safely?</h2>
              <p className="text-sm text-white/70 max-w-xl">
                Open the dApp, connect your wallet on Sepolia, and mint your first Cosmic Alchemists NFT with a
                builtin 7day refund option.
              </p>
            </div>
            <Button
              size="lg"
              className="px-8"
              onClick={() => {
                window.location.href = '/dapp'
              }}
            >
              <span className="flex items-center gap-2">
                Go to dApp
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 mt-16">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-xs text-white/40">
          NFTWebToken  b7 Cosmic Alchemists  b7 Educational Web3 Experience
        </div>
      </footer>
    </div>
  )
}
